import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import { useAlbums } from '../context/AlbumsContext';
import Navbar from '../components/Navbar';
import CarouselAlbum from '../components/CarouselAlbum';
import ModalCriarAlbum from '../components/ModalCriarAlbum';
import ModalConfirmarExclusao from '../components/ModalConfirmarExclusao';
import ModalRecuperarProgresso from '../components/ModalRecuperarProgresso';
import TelaVazia from '../components/TelaVazia';
import ScannerSticker from './ScannerSticker';
import Logo from '../images/caca_figurinhas_logo_v2.svg';
import './MeusAlbuns.css';

const MeusAlbuns = ({ onNavigate }) => {
  const {
    albums, activeAlbumId, isLoading, createAlbum, deleteAlbum, setAlbumActive, getAlbumProgress,
    userId, recoveryState, startRecovery, cancelRecovery, submitRecoveryId, applyRecovery, keepLocalData,
  } = useAlbums();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [selectedAlbumForDelete, setSelectedAlbumForDelete] = useState(null);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isUserIdMinimized, setIsUserIdMinimized] = useState(
    () => localStorage.getItem('cacafigurinhas:userIdMinimized') === 'true'
  );

  const toggleUserIdMinimized = () => {
    setIsUserIdMinimized((prev) => {
      localStorage.setItem('cacafigurinhas:userIdMinimized', String(!prev));
      return !prev;
    });
  };

  // Navegação do carousel
  const handleNext = () => {
    if (currentIndex < albums.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Modal de criação
  const handleCreateClick = () => {
    ReactGA.event({
      category: 'UI',
      action: 'Create Album Modal Opened',
    });
    setIsModalCreateOpen(true);
  };

  const handleCreateAlbum = async (name) => {
    try {
      createAlbum(name);
      // Move para o novo álbum
      setCurrentIndex(albums.length);
      setIsModalCreateOpen(false);
    } catch (error) {
      throw error;
    }
  };

  // Modal de exclusão
  const handleDeleteClick = (albumId, albumName) => {
    ReactGA.event({
      category: 'UI',
      action: 'Delete Album Modal Opened',
      label: albumName,
    });
    setSelectedAlbumForDelete({ id: albumId, name: albumName });
    setIsModalDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedAlbumForDelete) {
      deleteAlbum(selectedAlbumForDelete.id);
      setIsModalDeleteOpen(false);
      setSelectedAlbumForDelete(null);

      // Ajusta o index se deletou o último álbum
      if (currentIndex >= albums.length - 1 && albums.length > 1) {
        setCurrentIndex(albums.length - 2);
      } else if (albums.length === 1) {
        setCurrentIndex(0);
      }
    }
  };

  const handleSelectAlbum = (albumId) => {
    setAlbumActive(albumId);
    onNavigate?.('figurinhas');
  };

  return (
    <div className="meus-albuns">
      <header className="meus-albuns-header">
        <div className="header-top">
          <img src={Logo} alt="Caça Figurinhas Logo" className="logo-icon" />
          <h1 className="app-title">Caça Figurinhas</h1>
        </div>
        <p className="credits">Feito por Júlia Santos e Papai</p>
        {userId && (
          <div className={`user-id-section ${isUserIdMinimized ? 'user-id-section--minimized' : ''}`}>
            <button className="user-id-toggle" onClick={toggleUserIdMinimized} aria-label={isUserIdMinimized ? 'Expandir' : 'Minimizar'}>
              <span className="user-id-toggle-label">🔑 Meu ID de progresso</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`user-id-chevron ${isUserIdMinimized ? '' : 'user-id-chevron--open'}`}>
                <path d="m6 9 6 6 6-6"/>
              </svg>
            </button>
            {!isUserIdMinimized && (
              <>
                <p className="user-id-label">
                  Seu ID: <span className="user-id-value">{userId}</span>
                </p>
                <p className="user-id-hint">🔒 Guarde este ID — ele recupera seu progresso em qualquer dispositivo. Não compartilhe com ninguém.</p>
                <button className="btn-recuperar" onClick={startRecovery}>
                  Recuperar meu progresso
                </button>
              </>
            )}
          </div>
        )}
      </header>

      <Navbar activeTab="albums" onNavigate={onNavigate} activeAlbumId={activeAlbumId} isLoading={isLoading} />

      <main className="meus-albuns-main">
        <div className="meus-albuns-container">
          {isLoading ? (
            <div className="meus-albuns-loading">
              <div className="meus-albuns-loading-spinner" />
              <p>Carregando seus álbuns...</p>
            </div>
          ) : albums.length === 0 ? (
            <TelaVazia onCreateClick={handleCreateClick} />
          ) : (
            <>
              <CarouselAlbum
                albums={albums}
                currentIndex={currentIndex}
                onNext={handleNext}
                onPrevious={handlePrevious}
                onSelectAlbum={handleSelectAlbum}
                onDeleteClick={handleDeleteClick}
                getAlbumProgress={getAlbumProgress}
              />

              <div className="album-actions">
                <button
                  className="btn-criar-album"
                  onClick={handleCreateClick}
                  title="Criar novo álbum"
                >
                  Criar Álbum
                </button>

                {activeAlbumId && (
                  <button
                    className="btn-scanner"
                    onClick={() => {
                      setIsScannerOpen(true);
                      ReactGA.event({ category: 'Scanner', action: 'Button Clicked' });
                    }}
                    title="Abrir scanner de figurinhas"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"></path>
                      <circle cx="12" cy="13" r="3"></circle>
                    </svg>
                    Escanear figurinha
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Scanner em overlay */}
      {isScannerOpen && (
        <div className="scanner-overlay-full">
          <button
            className="scanner-close-btn"
            onClick={() => setIsScannerOpen(false)}
            title="Fechar scanner"
          >
            ✕
          </button>
          <ScannerSticker onNavigate={() => {}} />
        </div>
      )}

      {/* Modal recuperar progresso */}
      <ModalRecuperarProgresso
        recoveryState={recoveryState}
        onSubmitId={submitRecoveryId}
        onApplyCloud={applyRecovery}
        onKeepLocal={keepLocalData}
        onCancel={cancelRecovery}
      />

      {/* Modais */}
      <ModalCriarAlbum
        isOpen={isModalCreateOpen}
        onClose={() => setIsModalCreateOpen(false)}
        onCreateAlbum={handleCreateAlbum}
      />

      <ModalConfirmarExclusao
        isOpen={isModalDeleteOpen}
        onClose={() => {
          setIsModalDeleteOpen(false);
          setSelectedAlbumForDelete(null);
        }}
        onConfirmDelete={handleConfirmDelete}
        albumName={selectedAlbumForDelete?.name || ''}
      />
    </div>
  );
};

export default MeusAlbuns;
